import get from 'lodash/get';
import uniq from 'lodash/uniq';
import { CLINIC_LIST_MAX_NUMBER } from 'modules/utils/ConfigUtils';
import { stringCompare } from 'modules/utils/StringUtils';
import { createSelector } from 'reselect';
import {
  currentAccountConsumerIdSelector,
  currentAccountConsumerSelector
} from 'store/account/selectors';
import * as constants from 'store/findProvider/constants';
import * as docSelectors from 'store/myDoctors/selectors';
import { RootState } from 'store/types';
import { oc } from 'ts-optchain';
import { isDfdAuthenticatedSelector } from 'store/authentication/selectors';
import { isSelectHealthMemberSelector } from 'store/profile/selectors';
import * as types from 'store/findProvider/types';

export const findProviderSelector = (state: RootState) => state.findProvider;
/**
 * Insurances
 */
export const insurancesFetchingSelector = (state: RootState): boolean =>
  get(state, 'findProvider.insurances.isFetching', false);
export const insurancesSelector = (state: RootState): types.Insurance[] =>
  get(state, 'findProvider.insurances.data') || [];
export const insuranceNameSelector = createSelector(insurancesSelector, (insurances): string[] =>
  uniq(insurances.map(ins => ins.payerName))
);
/**
 * Hospitals
 */
export const getHospitalsFetching = (state: RootState): boolean =>
  get(state, 'findProvider.hospitals.isFetching', false);
export const getHospitals = (state: RootState): types.AffiliatedHospital[] =>
  get(state, 'findProvider.hospitals.data') || [];

/**
 * Clinics
 */
export const getClinicsFetching = (state: RootState): boolean =>
  get(state, 'findProvider.clinics.isFetching', false);
export const getRawClinics = (state: RootState): types.BaseClinics[] =>
  get(state, 'findProvider.clinics.data') || [];
export const getClinics = (state: RootState): types.BaseClinics[] =>
  getRawClinics(state).slice(0, CLINIC_LIST_MAX_NUMBER); // Over 2032 clinics originally, it will be reduced to 183

/**
 * Provider Specialties
 */
export const specialtiesFetchingSelector = (state: RootState): boolean =>
  get(state, 'findProvider.specialties.isFetching', false);
export const specialtiesDataSelector = (state: RootState): types.ProviderFacets =>
  get(state, 'findProvider.specialties.data') || [];
export const specialtiesSelector = createSelector(
  specialtiesDataSelector,
  (facets): types.ProviderSpecialties[] => {
    if (facets['specialties/specialtyName']) {
      return facets['specialties/specialtyName']
        .map((specialty: types.FacetDocument) => ({
          group: 'specialty',
          value: specialty.value
        }))
        .sort((a, b) => {
          const nameA = a.value.toUpperCase(); // ignore upper and lowercase
          const nameB = b.value.toUpperCase(); // ignore upper and lowercase
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        });
    }
    return [];
  }
);

/**
 * Languages
 */
export const getLanguagesFetching = (state: RootState): boolean =>
  get(state, 'findProvider.languages.isFetching', false);
export const getLanguages = (state: RootState): types.Language[] =>
  get(state, 'findProvider.languages.data') || [];

/**
 * Disclaimer
 */
export const hasAcceptedTC = (state: RootState) => {
  const isConsumerWithValidTerms = oc(state)
    .profile.consumer.externalTermsAcceptance([])
    .find(terms => {
      return Object.values(terms).includes('PROVIDER_SEARCH');
    });

  const hasTempTerms = oc(state).global.temporaryTCAccept.PROVIDER_SEARCH(false);
  return isConsumerWithValidTerms || hasTempTerms;
};
export const hasDeclinedTC = (state: RootState) =>
  get(state, 'findProvider.disclaimer.hasDeclinedTC', false);

/**
 *   Search Filter
 */
export const authenticatedSelectHealthMemberSelector = createSelector(
  [isDfdAuthenticatedSelector, isSelectHealthMemberSelector],
  (authenticated, selectHealthMember) => authenticated && selectHealthMember
);

export const searchFilterSelector = createSelector(
  findProviderSelector,
  findProvider => findProvider.searchFilter
);

export const activeFiltersSelector = createSelector(
  searchFilterSelector,
  searchFilter => searchFilter.activeFilters
);

export const temporaryFiltersSelector = createSelector(
  searchFilterSelector,
  searchFilter => searchFilter.temporaryFilters
);

export const getSortBy = createSelector(searchFilterSelector, searchFilter => searchFilter.sortBy);

export const getGeoLocation = createSelector(
  searchFilterSelector,
  searchFilter => searchFilter.geoLocation
);
/**
 * Search Result
 */
export const getProviderSearchFilter = (state: RootState) =>
  get(state, 'findProvider.searchFilter', {});
export const providerSearchFetchingSelector = (state: RootState): boolean =>
  get(state, 'findProvider.searchResult.isFetching', false);
export const providerSearchHydratingSelector = (state: RootState): boolean =>
  get(state, 'findProvider.searchResult.isHydrating', false);
export const providerSearchTotalCountSelector = (state: RootState): number =>
  get(state, 'findProvider.searchResult.totalResults') || 0;
export const providerRawSearchResultSelector = (state: RootState): types.ProviderSummary[] =>
  get(state, 'findProvider.searchResult.providers') || [];
export const providerSearchResultNotesSelector = (state: RootState): types.SearchResultNote[] =>
  get(state, 'findProvider.searchResult.notes') || [];
export const providerSearchRawTotal = (state: RootState): number =>
  get(state, 'findProvider.searchResult.rawProviderTotal') || 0;
export const providerSearchCurrentTotal = (state: RootState): number =>
  get(state, 'findProvider.searchResult.rawProviderCount') || 0;
export const providerSearchTotalRequested = (state: RootState): number =>
  get(state, 'findProvider.searchResult.totalRequested') || 0;

export const hasMoreProvidersSelector = createSelector(
  [providerSearchRawTotal, providerSearchTotalRequested],
  (rowTotal: number, totalRequested: number): boolean => !!(rowTotal - totalRequested)
);

export const providerSearchResultSelector = createSelector(
  providerRawSearchResultSelector,
  (providers: types.ProviderSummary[]): types.ProviderSummary[] =>
    providers.filter((provider: types.ProviderSummary) => provider.providerId)
);

export const bestMatchProvidersSelector = createSelector(
  providerSearchResultSelector,
  (providers): types.ProviderSummary[] => providers.slice(0, constants.BEST_MATCH_NUMBER)
);

export const sortedMatchProvidersSelector = createSelector(
  providerSearchResultSelector,
  (providers): types.ProviderSummary[] => providers.slice(constants.BEST_MATCH_NUMBER)
);

export const providerSearchResultErrorNotesSelector = createSelector(
  providerSearchResultNotesSelector,
  (errors: types.SearchResultNote[]): types.SearchResultNote[] =>
    errors.filter(err => err.type === 'error')
);

/**
 * Search Suggestions
 */
export const suggestionsFetchingSelector = (state: RootState): boolean =>
  get(state, 'findProvider.suggestions.isFetching', false);
export const suggestionsDataSelector = (state: RootState): types.SuggestionDocument[] =>
  get(state, 'findProvider.suggestions.data') || [];
export const suggestionsSelector = createSelector(suggestionsDataSelector, (suggestions): Partial<
  types.ProviderSummary
>[] => {
  if (suggestions.length > 0) {
    return suggestions.map((suggestion: types.SuggestionDocument) => ({
      displayName: suggestion['@search.text'],
      providerId: suggestion.corpProvId
    }));
  }
  return [];
});

/**
 * Provider Detail
 */
export const providerSearchPageNumberSelector = (state: RootState): number =>
  get(state, 'findProvider.searchResult.pageNumber', constants.START_PAGE_NUMBER);
export const providerDetailSelector = (state: RootState): types.ProviderDetail =>
  get(state, 'findProvider.providerDetails') || {};
export const providerDetailFetchingSelector = (state: RootState): boolean =>
  get(state, 'findProvider.providerDetails.isFetching', false);
export const providerSelector = (state: RootState) =>
  get(state, 'findProvider.providerDetails.provider') || {};
export const providerCorpIdSelector = (state: RootState): string =>
  get(state, 'findProvider.providerDetails.provider.corpProvId');
export const providerCernerIdSelector = (state: RootState): string =>
  get(state, 'findProvider.providerDetails.provider.cernerResourceId');
export const providerIsAcceptingOnlineBookingSelector = (state: RootState): boolean =>
  get(state, 'findProvider.providerDetails.provider.isAcceptingOnlineBooking');
export const providerHasOnlineNpvSlotsSelector = (state: RootState): boolean =>
  get(state, 'findProvider.providerDetails.provider.hasOnlineNpvSlots');
export const callClinicForNPVSelector = (state: RootState): boolean =>
  get(state, 'findProvider.providerDetails.provider.callClinicForNPV');
export const commentsTotalCountSelector = (state: RootState): number =>
  get(state, 'findProvider.providerDetails.provider.ratings.totalCommentCount');
export const providerPrimaryAffiliatedHospitalsSelector = (
  state: RootState
): types.AffiliatedHospital =>
  get(state, 'findProvider.providerDetails.provider.primaryHospitalAffiliation');
export const providerAffiliatedHospitalsSelector = (state: RootState): types.AffiliatedHospital[] =>
  get(state, 'findProvider.providerDetails.provider.hospitalAffiliations') || [];

export const providerAllAffiliatedHospitalsSelector = createSelector(
  [providerPrimaryAffiliatedHospitalsSelector, providerAffiliatedHospitalsSelector],
  (primary, hospitals) => (primary ? [primary, ...hospitals] : hospitals)
);

export const commentsFetchingSelector = (state: RootState): number =>
  get(state, 'findProvider.providerComments.isFetching') || false;
export const commentsPageNumberSelector = (state: RootState): number =>
  get(state, 'findProvider.providerComments.commentPageNumber') || constants.START_PAGE_NUMBER;
export const providerCommentsSelector = (state: RootState): types.Comment[] =>
  get(state, 'findProvider.providerComments.comments') || [];

export const totalCommentsSelector = createSelector(
  [commentsTotalCountSelector, providerCommentsSelector],
  (total, comments): number => total || comments.length
);

/**
 * Meta Data
 */
export const getProviderMetadata = (state: RootState) => get(state, 'findProvider.metadata');

export const getProviderLocations = createSelector(
  providerSelector,
  (provider): types.Location[] => {
    const locationsArray: types.Location[] = [];
    const primaryLocation: types.Location = oc(provider).primaryLocation();
    if (primaryLocation) locationsArray.push(primaryLocation);

    return locationsArray.concat(oc(provider).locations([]));
  }
);

export const providerRolledUpExceptionsSelector = createSelector(
  providerSelector,
  (provider): types.RolledUpException[] => get(provider, 'rolledUpExceptions') || []
);

export const providerFromRouteSelector = createSelector(
  getProviderMetadata,
  metadata => metadata.fromRoute
);

/**
 * Provider Account Switcher
 */
export const selectedConsumerPcpDoctorsSelector = createSelector(
  [
    docSelectors.pcpDoctorConsumerSelector,
    docSelectors.doctorSummaryListSelector,
    currentAccountConsumerIdSelector
  ],
  docSelectors.getDoctorListForSelector
);

export const selectedConsumerPastDoctorsSelector = createSelector(
  [
    docSelectors.pastDoctorConsumerSelector,
    docSelectors.doctorSummaryListSelector,
    currentAccountConsumerIdSelector
  ],
  docSelectors.getDoctorListForSelector
);

/**
 * Determines if from a provider summary and provider detail, the two doctors are equivalent (by checking for matching ids)
 */
const isSameDoctor = (
  providerSummary: types.ProviderSummary,
  providerDetail: types.ProviderDetail
) =>
  stringCompare(providerSummary.cernerResourceId, providerDetail.cernerResourceId, true) ||
  stringCompare(providerSummary.cernerPersonnelId, providerDetail.cernerPersonnelId, true) ||
  stringCompare(providerSummary.corpProvId, providerDetail.corpProvId, true) ||
  stringCompare(providerSummary.npi, providerDetail.npi, true) ||
  stringCompare(providerSummary.ncid, providerDetail.ncid, true);

export const isProviderPcpProviderSelector = createSelector(
  [providerSelector, currentAccountConsumerSelector, docSelectors.pcpDoctorConsumerSelector],
  (provider, consumer, pcpProviders) => {
    const pcpProviderBrief = pcpProviders.find(p => isSameDoctor(p, provider));

    if (!pcpProviderBrief || !pcpProviderBrief.consumerIdList) {
      return false;
    }

    return pcpProviderBrief.consumerIdList.includes(consumer.consumerId);
  }
);
